import React, { useEffect, useState, useRef } from "react";
import DynamicForm from "../../stories/Forms/DynamicForm";
import {
  confirmDetailsFields,
  confirmDetailsFieldsDna,
  confirmDetailsFieldsSh,
  phlebotomyConfirmDetailFields,
} from "../../stories/Forms/fields";
import useApis from "../../services/useAPI";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  personalDetailsDnaSchema,
  personalDetailsSchema,
  personalDetailsShSchema,
  phlebotomyDetailsSchema,
} from "../../stories/Forms/validationSchema";
import GenderWarningModal from "../../stories/Modal/GenderWarningModal";
import { as_title, getTestName, getTestTypeValueFromUrl } from "../../utils";
import { ArrowRightCircleFill, XSquare } from "react-bootstrap-icons";
import { useAuth } from "../../components/AuthContext/AuthContext";
import {
  accessError,
  phlebotomyAssessmentStatusUrl, postCoderurl
} from "../../utils/constants";
import { Spinner, Accordion, Modal, Button } from "react-bootstrap";
import { ErrorDisplay } from "../Errors/ErrorDisplay";
import {
  formattedPhoneNumber, calculateProgressSteps,
  updateProgressAfterCompletion, fetchAddressOptions, removeCountryCode, removeCountryCodeForUkNumber, getCountryFromPhone
} from "../../utils/utils";
import StepProgressBar from "../../stories/ProgressBars/ProgressBar";
import NavigateButtons from "../../stories/Buttons/NavigateButtons";
import { useMediaQuery } from "react-responsive";

/**
 * ConfirmDetails component
 *
 * This component handles the confirmation of personal details for activating test kits.
 * It fetches existing personal details, allows the user to update them through a form,
 * and submits the data to activate the test kit.
 *
 * @returns {JSX.Element} The rendered component for confirming personal details.
 */

let allowPost = false;
let isConfirmed = false;
function ConfirmDetails() {
  const { primaryBarcode } = useParams();
  const [validationSchema, setValidationSchema] = useState(
    personalDetailsSchema,
  );
  const [genderWarning, setGenderWarning] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const auth = useAuth();
  const barcodeValue = primaryBarcode?.primaryBarcode || primaryBarcode;
  const getApiUrl = `/api/dashboard/activate/personal-details/${primaryBarcode}/`;
  const typeUrl = `/api/dashboard/check-test/${barcodeValue}/`;
  const postApiUrl = `/api/dashboard/activate/personal-details`;
  const postActivatingUrl = `/api/dashboard/activating/`;
  const [addressOptions, setAddressOptions] = useState([]);
  const { callApi, serverError, resultGet, resultPost } = useApis();
  const { callApi: callDetailsApi, resultGet: detailsResult, serverError: detailsServerError } = useApis();
  const { callApi: callUpdateUserApi, resultPost : updateUserResultPost, resultGet : updateUserResult, serverError: updateUserServerError } = useApis();
  const {
  callApi: callPhleboApi,
  resultGet: phleboResult,
} = useApis();
  const addressLoading = false;

  const [fields, setFields] = useState(confirmDetailsFields);
  // email_mfa from the backend: true means the saved number is non-UK
  const [savedEmailMfa, setSavedEmailMfa] = useState(false);
  const { test_type, bid } = useParams();
  const [selectedSex, setSelectedSex] = useState("");
  const [data, setData] = useState();
  const [personalDetails, setPersonalDetails] = useState(null);
  const submitAction = useRef(null);
  const location = useLocation();
  const edit = location?.search?.includes("edit=true");
  const isBootsUrl = location?.pathname?.inc;
  const formikRef = useRef(null);
  const personalDetailsformikRef = useRef(null);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmResolver, setConfirmResolver] = useState(null);
  const handleConfirmationModalClose = () => setShowConfirmModal(false);
  const handleConfirmationModalShow = () => setShowConfirmModal(true);
  const emailMfa = sessionStorage.getItem("email_mfa") === "true";
  // Checks for isPhlebotomy flag
  const isPhlebotomy = globalThis.location.pathname.includes("/boots");
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const targetFieldNames = ["address_line_1", "address_line_2", "home_city"];

  let enabledFields = fields.map((field) => {
    if (targetFieldNames.includes(field.name)) return { ...field, disabled: false };
    if (field.name === "phone_number_primary") {
      return { ...field, clearOnNonUkSwitch: true, savedNumberIsNonUk: savedEmailMfa === true };
    }
    return field;
  });

  if (auth.isNonUKNumber) {
  enabledFields = enabledFields.map((field) => {
    if (field.name === "phone_number_primary") {
      return {
        ...field,
        type: "advancePhoneNumber",
        controlId: "advancePhoneNumber",
        handleChange: (e, setFieldValue) => setFieldValue(field.name, e.target.value),
      };
    }
    return field;
  });
}
  // First check activation stage
  useEffect(() => {
    if (barcodeValue && !test_type && !bid) {
      callApi({}, typeUrl, "GET");
    }
  }, [barcodeValue]);

  // Validate activation stage - updated to allow "consent" stage as well
  useEffect(() => {
    if (resultGet?.activation_stage === null) {
      setError(accessError);
      return;
    }
    const newProgressSteps = calculateProgressSteps(
      resultGet,
      resultGet?.type,
      location.pathname,
    );
    auth?.setProgressSteps(newProgressSteps);
    if (resultGet?.activation_stage !== undefined) {
      const allowedStages = [
        "consent",
        "personal-details",
        "collect-sample",
        "sh-consent",
        "barcode",
        "secondary-barcode"
      ];
      if (
        resultGet.activation_stage !== null &&
        !allowedStages.includes(resultGet.activation_stage)
      ) {
        setError(accessError);
      }
    }
  }, [resultGet]);

  // Fetch personal details
  useEffect(() => {
    if (!test_type && !error) {
      callDetailsApi({}, getApiUrl, "GET");
    }
    if (detailsServerError) {
      setError(detailsServerError);
    }
  }, [error]);

  const handleCodeBlur = async (code, setFieldValue) => {
    const trimmedCode = code.replace(/\s+/g, "");

    // Early return if empty
    if (!trimmedCode) {
      setFieldValue("home_street_address", "");
      setAddressOptions([]);
      return;
    }
    await fetchAddressOptions({
      trimmedCode,
      callApi,
      postCoderurl,
      setAddressOptions,
      setFieldValue,
    });
  };

  // Process fetched personal details
  useEffect(() => {
    // Only process personal details if we have data and don't have an error
    if (detailsResult && !error && detailsResult.first_name !== undefined) {
      auth.setIsNonUKNumber(detailsResult.email_mfa);
      setSavedEmailMfa(detailsResult.email_mfa);
      auth.setPrimaryPhoneNumber(detailsResult.phone_number_primary);
      setPersonalDetails(detailsResult);
      if (
        detailsResult?.type?.startsWith("dna-") ||
        detailsResult?.type?.startsWith("boots-dna-")
      ) {
        setFields(confirmDetailsFieldsDna);
        setValidationSchema(personalDetailsDnaSchema);
      } else if (detailsResult?.secondary_barcode_type === "urine") {
        setFields(confirmDetailsFieldsSh);
        setValidationSchema(personalDetailsShSchema);
      } else {
        setFields(confirmDetailsFields);
        setValidationSchema(personalDetailsSchema);
      }

      // Store data in localStorage
      if (!test_type && !bid) {
        localStorage.setItem("test_kit_id", detailsResult?.test_kit_id);
        localStorage.setItem("user_id", detailsResult?.customer_id);
        localStorage.setItem(
          "secondary_barcode_type",
          detailsResult?.secondary_barcode_type,
        );
      }
    }
  }, [detailsResult]);

  useEffect(() => {
    if (isPhlebotomy && test_type) {
      setFields(confirmDetailsFields);
      setValidationSchema(personalDetailsSchema);
    }
  }, []);

  useEffect(() => {
    if (isPhlebotomy && test_type && bid) {
      callPhleboApi(null, phlebotomyAssessmentStatusUrl(bid), "GET");
    }
  }, []);

  useEffect(()=>{
    if(updateUserResultPost){
         auth?.showToast(
          "Confirm user details",
          "User details updated successfully",
          "../assets/mhc.png",
          <ArrowRightCircleFill />,
        );
    }else if(updateUserServerError){
       auth?.showToast(
          "Error",
          updateUserServerError,
          "../assets/mhc.png",
          <XSquare />,
        );
    }
  },[updateUserResultPost,updateUserServerError]);

  useEffect(() => {
    setError(error || serverError);
  }, [error, serverError]);

  useEffect(()=>{
    if(serverError === "Postcode lookup failed. Please try again later." ||
    serverError === "Invalid postcode format"){
      auth?.showToast(
          "Error",
          serverError,
          "../assets/mhc.png",
          <XSquare />,
        );
    }
  },[serverError])
  
  useEffect(() => {
    if ((resultPost && !isActivated) || updateUserResultPost) {
      setIsActivated(true);

      if (!genderWarning) {
        const newProgressSteps = updateProgressAfterCompletion(
          data,
          isPhlebotomy ? test_type : resultGet?.type,
          location.pathname,
        );

        auth?.setProgressSteps(newProgressSteps);

        if (shouldNavigate) {
          if (isPhlebotomy) {
           navigateToPhlebotomySuccess()
          } else {
            awaitNavigateToNextStep()
          }
        }
      }
    }
  }, [resultPost, genderWarning, isActivated, shouldNavigate, isPhlebotomy, test_type, edit, bid, updateUserResultPost]);

  const navigateToPhlebotomySuccess = () => {
    const path = edit
      ? `/bloods/boots/success-page/${test_type}/${bid}`
      : `/bloods/boots/assessment/${test_type}/${bid}/1`;
    navigate(path);
  };

  const awaitNavigateToNextStep = async () => {
    const nextStep = resultGet?.type?.includes("sex")
      ? `/sh-consent/${primaryBarcode}`
      : `/time-administered/${primaryBarcode}`;

    const activatingPayload = {
      activation_stage: "personal-details",
      code: primaryBarcode,
    };

    (async () => {
      await callApi(activatingPayload, postActivatingUrl, "POST");
      navigate(nextStep);
    })();
  };

  const checkGenderWarnings = (selectedSex, testType) => {
    if (
      (personalDetails?.type === "blood-erectile" ||
        testType === "blood-male-sex-health") &&
      selectedSex === "female"
    ) {
      setGenderWarning(true);
      return true;
    }

    if (
      [
        "blood-female-sex-health",
        "blood-menopause",
        "blood-polycystic",
      ].includes(testType) &&
      selectedSex === "male"
    ) {
      setGenderWarning(true);
      return true;
    }

    return false;
  };
  const handleSubmit = async (formData, shouldNavigateToNext = false, isFinalSubmit = false) => {
    if (formData.home_street_address !== "No results") {
      formData.address_line_1 = "";
      formData.address_line_2 = "";
      formData.home_city = "";
      formData.home_country = "";
    }
   const rawNumber = formData.phone_number_primary.trim().replace(/\s+/g, "");

   // Strip out any country code that might already be embedded in the number itself
   const bareNumber = removeCountryCodeForUkNumber(rawNumber).replace(
     /\s+/g,
     "",
   );

   const isUkPhoneField = formData.phone_number_primary_type === "phone";
   const countryCode = isUkPhoneField
     ? "+44"
     : formData.phone_number_primary_country ||
       `${getCountryFromPhone(rawNumber)?.code  !== undefined ? '+' :'+44'}${getCountryFromPhone(rawNumber)?.code || ""}`;

   formData.phone_number_primary = countryCode
     ? `${countryCode}${bareNumber}`
     : bareNumber;
    auth.setPrimaryPhoneNumber(formData.phone_number_primary);
    setSavedEmailMfa(auth.isNonUKNumber === true);

    isConfirmed = isFinalSubmit;
    initialFormValues = formData;
    setData({
      ...formData,
      phone_number_primary: auth.isNonUKNumber
        ? formData?.phone_number_primary
        : removeCountryCodeForUkNumber(formData?.phone_number_primary),
    });
    setShouldNavigate(shouldNavigateToNext);

    const selectedSex = formData.sex;
    setSelectedSex(selectedSex);

    const isPhlebo = Boolean(isPhlebotomy && test_type && bid);

    let processedFormData = {
      ...formData,
      // phone_number_primary:`${auth.isNonUKNumber ? "+44":formData.phone_number_primary_country }${formData.phone_number_primary.trim().replace(/\s+/g, "")}`,
      email_mfa: auth.isNonUKNumber ,
      ...(isPhlebo
        ? {
            bid,
            test_type,
            phlebotomist_test: true,
            ...(edit && { edit: true }),
          }
        : {
            barcode: primaryBarcode,
            ...((personalDetails?.type?.startsWith("dna") ||
              personalDetails?.type?.startsWith("boots-dna-")) && { type: "dna" }),
          }),
    };
    allowPost = checkGenderWarnings(
      selectedSex,
      isPhlebo ? test_type : personalDetails?.type
    );
    if (allowPost) {
      setShouldNavigate(false);
        return false;
    }
    


    setIsActivated(false);
    if (submitAction.current === "personal-details-save") {
      isConfirmed = await confirmToSave();
      if (!allowPost && isConfirmed) {
        callUpdateUserApi(processedFormData, postApiUrl, "POST");
      }
    }else{
         callUpdateUserApi(processedFormData, postApiUrl, "POST");
    }
    if (serverError) {
      setError(serverError);
    }
  };

  const confirmToSave = () => {
    return new Promise((resolve) => {
      setConfirmResolver(() => resolve);
      setShowConfirmModal(true);
    });
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    confirmResolver?.(true);
  };

  const handleCancel = () => {

    setShowConfirmModal(false);
    setData(initialFormValues);
  };
  const handleChange = (e, setFieldValue) => {
    const { name, value } = e.target;
    if (name === "home_street_address" && value === "No results") {
      setFieldValue("address_line_1", "");
      setFieldValue("address_line_2", "");
      setFieldValue("home_city", "");
      setFieldValue("home_country", "UK");
    }
    setFieldValue(name, value);
      setTimeout(() => {
  }, 0);

  }
  // Inline initial form values
  let initialFormValues = (() => {
    const isPhlebotomyCase =
      isPhlebotomy &&
      bid &&
      test_type &&
      phleboResult?.phlebotomist_booking_info;
    const isPersonalDetailsCase = !test_type && !bid && personalDetails;
    const resolveCountryCode = (rawPhone) => {
      if (!rawPhone) return "+44";
      const detected = getCountryFromPhone(rawPhone);
      return detected?.code ? `+${detected.code}` : "+44";
    };

    if (isPhlebotomyCase) {
      const bookingInfo = phleboResult?.phlebotomist_booking_info;
      return {
        first_name: bookingInfo.first_name,
        address_line_1: bookingInfo.address_line_1,
        address_line_2: bookingInfo.address_line_2,
        home_city: bookingInfo.home_city,
        home_country: "UK",
        last_name: bookingInfo.last_name,
        dob: bookingInfo.dob,
        sex: bookingInfo.sex,
        identify_with_gender: bookingInfo.identify_with_gender,
        ethnicity: bookingInfo.ethnicity,
        home_street_address: bookingInfo.home_street_address,
        home_postal_code: bookingInfo.home_postal_code,
        phone_number_primary: (bookingInfo.email_mfa ? bookingInfo.phone_number_primary : removeCountryCodeForUkNumber(bookingInfo.phone_number_primary)) || "",
        phone_number_primary_country: resolveCountryCode(bookingInfo.phone_number_primary),
      };
    } else if (isPersonalDetailsCase) {
      return {
        first_name: personalDetails.first_name,
        address_line_1: personalDetails.address_line_1,
        address_line_2: personalDetails.address_line_2,
        home_city: personalDetails.home_city,
        home_country: "UK",
        last_name: personalDetails.last_name,
        dob: personalDetails.dob,
        sex: personalDetails.sex,
        identify_with_gender: personalDetails.identify_with_gender,
        ethnicity: personalDetails.ethnicity,
        home_street_address: personalDetails.home_street_address,
        home_postal_code: personalDetails.home_postal_code,
        phone_number_primary: (personalDetails.email_mfa ? personalDetails.phone_number_primary : removeCountryCodeForUkNumber(personalDetails.phone_number_primary)) || "",
        phone_number_primary_country: resolveCountryCode(personalDetails.phone_number_primary),
      };
    } else {
      return data;
    }
  })();
  useEffect(()=>{
    if(phleboResult){
      auth.setIsNonUKNumber(phleboResult.phlebotomist_booking_info.email_mfa)
      setSavedEmailMfa(phleboResult.phlebotomist_booking_info.email_mfa)
      auth.setPrimaryPhoneNumber(phleboResult.phlebotomist_booking_info.phone_number_primary)
    }
  },[phleboResult])
  useEffect(() => {
    setData(initialFormValues);
  }, [detailsResult]);

  const toggleModal = () => {
    setGenderWarning(!genderWarning);
    handleCancel();
    if (genderWarning) {
      allowPost = false;
    }
  };

  const isPhlebo = isPhlebotomy && test_type && bid;

  if (!error && (
    (!isPhlebo && !resultGet && !personalDetails) ||
    (isPhlebo && !phleboResult)
  )) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  if (
  error &&
  error !== "Postcode lookup failed. Please try again later." &&
  error !== "Invalid postcode format" &&
  error !== "Please provide a valid UK mobile number."
) {
  return <ErrorDisplay error={error} consent={false} />;
}

  const testType = personalDetails?.type || test_type;
  const testName = personalDetails?.type_name || phleboResult?.phlebotomist_booking_info?.test_kit_name;
  
  return (
    
    <div className="row justify-content-center confirm-details-page">
      <div className="col-md-12">
        <StepProgressBar
          steps={auth?.progressSteps?.totalSteps || 5}
          currentStep={auth?.progressSteps?.currentStep || 2}
          completedSteps={auth?.progressSteps?.completedSteps || 1}
          isCompleted={auth?.progressSteps?.completedSteps || 1}
          is_assessment_completed={false}
        />
        {isBootsUrl ? (
          <>
            <p className="text-muted mt-4">
              <span className="text-dark ageo-bold">
                {" "}
                Test :{" "}
                {as_title(
                  `${getTestName(
                    getTestTypeValueFromUrl(testType || test_type),
                  )}`,
                )}{" "}
              </span>
            </p>
            <p className="text-muted">
              Please fill in the form to register your booking. You'll be able
              to edit these details until the day of your appointment.
            </p>
          </>
        ) : (
          <div className="test-type-heading">
            <p className={`text-muted ageo-medium ${isMobile ? "pb-2 fs-16":"pb-1 fs-18"}`}>
              Test :{" "}
              <span className="text-dark ageo-bold">
                {testName}
              </span>
            </p>
            <p className="text-muted subtitle ageo-medium">
              Please confirm the information below is correct
            </p>
          </div>
        )}
        <Accordion defaultActiveKey="0">
          <Accordion.Item eventKey="0">
            <Accordion.Header className="d-flex justify-content-between">
              Personal details
            </Accordion.Header>
            <Accordion.Body>
              {/* <pre>{JSON.stringify(enabledFields,null,2)}</pre> */}
              <DynamicForm
                formikRef={personalDetailsformikRef}
                fields={enabledFields}
                genderButton={true}
                initialValues={data || initialFormValues}
                onSubmit={(formData) => handleSubmit(formData, false,false)}
                validationSchema={validationSchema}
                handleCodeBlur={handleCodeBlur}
                addressLoading={addressLoading}
                addressOptions={addressOptions}
                handleConfirmationModalShow={handleConfirmationModalShow}
                submitAction={submitAction}
                isResetForm={false}
                handleChange={handleChange}
                validationError={""}
                className="col-md-12"
                isAssessmentCompletedForPhlebotomy={phleboResult?.is_assessment_completed}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        {genderWarning && (
          <GenderWarningModal
            heading={"Warning"}
            isOpen={genderWarning}
            toggle={toggleModal}
            message={
              <>
                {selectedSex === "female" ? (
                  <>
                    <p>
                      You have selected your gender at birth as <b>female</b>.
                      This test includes the measurement of markers
                      physiologically associated with people born <b>male</b>.
                    </p>
                    <p>
                      This test may not, therefore, be suitable for you. Please
                      contact customer care to exchange or refund your product
                      at{" "}
                      <a href="mailto:care@myhealthchecked.com">
                        care@myhealthchecked.com
                      </a>{" "}
                      or 0203 371 3537.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      You have selected your gender at birth as <b>male</b>.
                      This test includes the measurement of markers
                      physiologically associated with people born <b>female</b>.
                    </p>
                    <p>
                      This test may not, therefore, be suitable for you. Please
                      contact customer care to exchange or refund your product
                      at{" "}
                      <a href="mailto:care@myhealthchecked.com">
                        care@myhealthchecked.com
                      </a>{" "}
                      or 0203 371 3537.
                    </p>
                  </>
                )}
                <p />
              </>
            }
          />
        )}
        <NavigateButtons
          formikRef={personalDetailsformikRef}
          setShouldNavigate={setShouldNavigate}
          onContinue={() => {
            setShouldNavigate(true);
          }}
          submitAction={submitAction}
          handleSubmit={handleSubmit}
        />
        {!genderWarning && (
          <Modal show={showConfirmModal} onHide={handleCancel}>
            <Modal.Header closeButton>
              <Modal.Title>Personal Details</Modal.Title>
            </Modal.Header>
            <Modal.Body>You’ve updated your details. These changes will also update your account details. <br />Do you want to continue?</Modal.Body>
            <Modal.Footer>
              <Button className="btn-white" onClick={handleCancel}>
                Cancel
              </Button>
              <Button className="btn-green" onClick={handleConfirm}>
                Confirm & Continue
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </div>
  );
}

export default ConfirmDetails;
